<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { useManageGroup } from '../../composables/useManageGroup';
import { useLocale } from '../../composables/useLocale';
import useToast from '../../composables/useToast';
import NewGroupForm from './NewGroupForm.vue';
import { createErrorParser } from '../../error-utils';

const { t } = useLocale();
const { createGroup, isCreatingGroup: isLoading } = useManageGroup();
const { toast } = useToast();

type Props = {
  modelValue: boolean;
  organizationUid: string;
  initialName?: string;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:modelValue', open: boolean): void;
  (e: 'groupCreated', value: { uid: string; name: string }): void;
}>();

const form = ref<typeof NewGroupForm | null>(null);

watch(
  () => props.modelValue,
  (open) => {
    if (open && props.initialName) {
      nextTick(() => {
        if (form.value) form.value.name = props.initialName!;
      });
    }
  },
);

const onCancel = () => {
  emit('update:modelValue', false);
};

const errorParser = createErrorParser(t);

const onConfirm = async () => {
  if (!form.value?.name) return;
  const organizationId = props.organizationUid;
  const name = form.value.name;

  const newGroup = await createGroup(
    organizationId,
    name,
    form.value?.type,
    form.value?.yearsInSchool,
    false,
    form.value?.expiresAt ?? null,
  ).catch((err: unknown) => {
    const errorMessage = errorParser(err) ?? 'lcp.create_group_failed';
    form.value?.resetValidationAndValidate();
    toast(t(errorMessage), 'error');
    return null;
  });
  if (!newGroup) return;
  emit('groupCreated', { uid: newGroup.uid, name });
  emit('update:modelValue', false);
};
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="480"
    :persistent="isLoading"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card :title="t('lcp.create_new_group')">
      <v-card-text>
        <NewGroupForm ref="form" />
      </v-card-text>
      <v-card-actions class="justify-end ga-2">
        <v-btn :disabled="isLoading" variant="text" @click="onCancel">
          {{ t('lcp.cancel') }}
        </v-btn>
        <v-btn :loading="isLoading" :disabled="!form?.isValid" variant="flat" @click="onConfirm">
          {{ t('lcp.create_new_group') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
